/**
 * Texts — the Company's text messages, in the bar beside the phone
 * (#207 step 4).
 *
 * One trigger at the right of the nav; its panel is the house popup and
 * is one of two screens: the numbers the Company has texted with, each
 * with the last thing said, or one of those conversations with a reply
 * box under it. A new text to a number nobody has texted yet is the
 * conversation screen with the number typed in. A thread is the pair of
 * numbers and nothing is created to start one, so there is no "new
 * thread" step to get wrong.
 *
 * Mounted once, in `AppNav`, so an open conversation survives moving
 * between pages. It reads the API when it opens and every fifteen
 * seconds while it is open — a text that arrived while somebody was
 * reading the thread should appear without a reload, and nothing pushes
 * to the browser yet. It draws nothing where the deployment has no
 * carrier, which the API says with a 503.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { ApiError } from "@shared/api-client";
import { Button } from "./ui/Button";
import { ChevronLeftIcon, MessageSquareIcon } from "./ui/icons";
import { formatWhen } from "../lib/arrivals-format";
import { textsApi, type MessageThread, type TextMessage, type TextsApi } from "../lib/messages-api";
import { useDismissable } from "../lib/use-dismissable";
import field from "./ui/field.module.css";
import styles from "./Texts.module.css";

const POLL_MS = 15_000;

export interface TextsProps {
  /** The API. Defaults to the real one; a test passes a fake. */
  api?: TextsApi;
  /** How often an open panel re-reads. 0 turns it off. */
  pollMs?: number;
}

type Availability = "unknown" | "on" | "off";

interface Conversation {
  /** The other end, as typed or as the server normalised it. */
  with: string;
  /** The Company's number it is on; "" until the server says. */
  number: string;
}

export function Texts({ api = textsApi, pollMs = POLL_MS }: TextsProps) {
  const [availability, setAvailability] = useState<Availability>("unknown");
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState<MessageThread[]>([]);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<TextMessage[]>([]);
  const [loadError, setLoadError] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useDismissable({
    open,
    onDismiss: () => setOpen(false),
    onEscape: () => {
      setOpen(false);
      triggerRef.current?.focus();
    },
    pointer: [containerRef],
  });

  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const list = await api.listThreads(signal);
        if (signal?.aborted) return;
        setThreads(list);
        setAvailability("on");
        setLoadError("");
      } catch (err) {
        if (signal?.aborted) return;
        if (err instanceof ApiError && err.status === 503) {
          setAvailability("off");
          return;
        }
        setAvailability("on");
        setLoadError(err instanceof Error ? err.message : "Could not read texts");
      }
    },
    [api],
  );

  const refreshConversation = useCallback(
    async (c: Conversation, signal?: AbortSignal) => {
      try {
        const page = await api.listThread(c.with, c.number || undefined, signal);
        if (signal?.aborted) return;
        setMessages(page.messages);
        setConversation((cur) => (cur && cur.with === c.with ? { with: page.with, number: page.number } : cur));
        setLoadError("");
      } catch (err) {
        if (signal?.aborted) return;
        setLoadError(err instanceof Error ? err.message : "Could not read texts");
      }
    },
    [api],
  );

  // Once on mount, to learn whether texts exist here at all; then on
  // every open, and on a timer while open.
  useEffect(() => {
    const ctl = new AbortController();
    void refresh(ctl.signal);
    return () => ctl.abort();
  }, [refresh]);

  useEffect(() => {
    if (!open || availability !== "on") return;
    const ctl = new AbortController();
    const tick = () => {
      void refresh(ctl.signal);
      if (conversation) void refreshConversation(conversation, ctl.signal);
    };
    tick();
    if (pollMs <= 0) return () => ctl.abort();
    const id = setInterval(tick, pollMs);
    return () => {
      clearInterval(id);
      ctl.abort();
    };
    // conversation.with is the identity of the thread being read; the
    // number fills in from the server and must not restart the timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, availability, conversation?.with, pollMs, refresh, refreshConversation]);

  if (availability !== "on") return null;

  const openConversation = (c: Conversation) => {
    setMessages([]);
    setConversation(c);
    void refreshConversation(c);
  };

  const sent = (m: TextMessage) => {
    setMessages((cur) => [...cur, m]);
    void refresh();
  };

  return (
    <div ref={containerRef} className={styles.texts}>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Texts"
        onClick={() => setOpen((o) => !o)}
        className={styles.trigger}
      >
        <MessageSquareIcon className={styles.triggerIcon} />
      </button>
      {open && (
        <div role="dialog" aria-label="Texts" className={styles.panel}>
          {conversation ? (
            <ConversationScreen
              api={api}
              conversation={conversation}
              messages={messages}
              loadError={loadError}
              onBack={() => {
                setConversation(null);
                setMessages([]);
              }}
              onSent={sent}
            />
          ) : (
            <ThreadsScreen threads={threads} loadError={loadError} onOpen={openConversation} />
          )}
        </div>
      )}
    </div>
  );
}

function ThreadsScreen({
  threads,
  loadError,
  onOpen,
}: {
  threads: MessageThread[];
  loadError: string;
  onOpen: (c: Conversation) => void;
}) {
  const [composing, setComposing] = useState(false);
  const [number, setNumber] = useState("");

  const start = (e: FormEvent) => {
    e.preventDefault();
    if (!number.trim()) return;
    onOpen({ with: number.trim(), number: "" });
    setNumber("");
    setComposing(false);
  };

  return (
    <div className={styles.screen}>
      <div className={styles.head}>
        <p className={styles.title}>Texts</p>
        <Button variant="secondary" size="sm" onClick={() => setComposing((c) => !c)}>
          New text
        </Button>
      </div>
      {composing && (
        <form className={styles.compose} onSubmit={start}>
          <label className={field.label} htmlFor="texts-new-number">
            Number
          </label>
          <input
            id="texts-new-number"
            className={field.input}
            type="tel"
            inputMode="tel"
            autoComplete="off"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
          />
          <div className={styles.actions}>
            <Button type="submit" variant="primary" size="sm" disabled={!number.trim()}>
              Open
            </Button>
          </div>
        </form>
      )}
      {loadError && <p className={styles.error}>{loadError}</p>}
      {threads.length === 0 && !loadError ? (
        <p className={styles.empty}>No texts yet.</p>
      ) : (
        <div className={styles.threads} role="list">
          {threads.map((t) => (
            <button
              key={`${t.company_e164}|${t.counterparty_e164}`}
              type="button"
              role="listitem"
              className={styles.thread}
              onClick={() => onOpen({ with: t.counterparty_e164, number: t.company_e164 })}
            >
              <span className={styles.threadRow}>
                <span className={styles.threadNumber}>{t.counterparty_e164}</span>
                <span className={styles.threadWhen}>{formatWhen(t.last_at)}</span>
              </span>
              <span className={styles.threadLast}>
                {t.last_direction === "outbound" ? "You: " : ""}
                {t.last_body}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ConversationScreen({
  api,
  conversation,
  messages,
  loadError,
  onBack,
  onSent,
}: {
  api: TextsApi;
  conversation: Conversation;
  messages: TextMessage[];
  loadError: string;
  onBack: () => void;
  onSent: (m: TextMessage) => void;
}) {
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setSendError("");
    try {
      const m = await api.send({ to: conversation.with, body, from: conversation.number || undefined });
      setDraft("");
      onSent(m);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : "The text was not sent");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.screen}>
      <div className={styles.head}>
        <button type="button" className={styles.back} aria-label="All texts" onClick={onBack}>
          <ChevronLeftIcon className={styles.icon} />
        </button>
        <p className={styles.title}>{conversation.with}</p>
        <span />
      </div>
      {conversation.number && <p className={styles.caption}>From {conversation.number}</p>}
      {loadError && <p className={styles.error}>{loadError}</p>}
      <div className={styles.messages} role="log" aria-label="Messages">
        {messages.length === 0 && !loadError && <p className={styles.empty}>Nothing said yet.</p>}
        {messages.map((m) => (
          <div key={m.id} className={`${styles.message} ${m.direction === "inbound" ? styles.inbound : styles.outbound}`}>
            <div className={styles.bubble}>{m.body}</div>
            <span className={`${styles.meta} ${m.status === "failed" ? styles.failed : ""}`}>
              {formatWhen(m.created_at)}
              {m.status === "failed" ? ` · Not sent${m.error ? `: ${m.error}` : ""}` : ""}
            </span>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form className={styles.compose} onSubmit={(e) => void submit(e)}>
        <label className={field.label} htmlFor="texts-reply">
          Message
        </label>
        <textarea
          id="texts-reply"
          className={field.input}
          rows={2}
          maxLength={1000}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        {sendError && <p className={styles.error}>{sendError}</p>}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" size="sm" disabled={!draft.trim()} loading={sending}>
            Send
          </Button>
        </div>
      </form>
    </div>
  );
}
